import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ExclusiveContentSubscriptionsState } from '@/domain/entities/exclusiveContentSubscription/exclusiveContentSubscriptionsState';
import type { ExclusiveContentSubscriptionEntity } from '@/domain/entities/exclusiveContentSubscription/exclusiveContentSubscriptionEntity';
import type { RootState } from '@/presentation/store';
import { ExclusiveContentSubscriptionStatus } from '@/data/enums/exclusiveSubscriptions/exclusiveContentSubscriptionStatus';

const initialState: ExclusiveContentSubscriptionsState = {
  subscriptions: [],
  isLoaded: false,
};

const exclusiveContentSlice = createSlice({
  name: 'exclusiveContent',
  initialState,
  reducers: {
    setExclusiveContentSubscriptions: (state, action: PayloadAction<ExclusiveContentSubscriptionEntity[]>) => {
      state.subscriptions = action.payload;
      state.isLoaded = true;
    },
    clearExclusiveContentSubscriptions: (state) => {
      state.subscriptions = [];
      state.isLoaded = false;
    },
  },
});

export const {
  setExclusiveContentSubscriptions,
  clearExclusiveContentSubscriptions,
} = exclusiveContentSlice.actions;

export default exclusiveContentSlice.reducer;

// --- Selectors (replicate Flutter's ExclusiveContentNotifier methods) ---

export const selectExclusiveContentSubscriptions = (
  state: RootState,
): ExclusiveContentSubscriptionEntity[] => state.exclusiveContent.subscriptions;

export const selectExclusiveContentLoaded = (state: RootState): boolean =>
  state.exclusiveContent.isLoaded;

/**
 * Mirrors Flutter's ExclusiveContentNotifier.hasActiveSubscription.
 * Statuses that grant access: active (1) or cancelled (3).
 */
export const selectHasActiveSubscription = (state: RootState, creatorUserId: string): boolean => {
  const subscription = state.exclusiveContent.subscriptions.find(
    (s) => s.userId === creatorUserId,
  );
  if (!subscription) return false;
  return (
    subscription.status === ExclusiveContentSubscriptionStatus.active ||
    subscription.status === ExclusiveContentSubscriptionStatus.cancelled
  );
};

/**
 * Mirrors Flutter's ExclusiveContentNotifier.getSubscriptionStatus.
 * Returns inactive when no subscription exists for the creator.
 */
export const selectExclusiveContentSubscriptionStatus = (
  state: RootState,
  creatorUserId: string,
): ExclusiveContentSubscriptionStatus => {
  const subscription = state.exclusiveContent.subscriptions.find(
    (s) => s.userId === creatorUserId,
  );
  if (!subscription) return ExclusiveContentSubscriptionStatus.inactive;
  return subscription.status as ExclusiveContentSubscriptionStatus;
};
